import { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { Button, Icon } from "@common/index";
import { ComponentColor } from "@common/types";
import { useAuth } from "@/hooks/useAuth";
import { useCanShop } from "@/hooks/useCanShop";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";
import { useSignOut } from "@/hooks/useSignOut";
import { signInRedirect } from "@components/Auth/signInRedirect";
import { ROUTES } from "@routing/routes";
import { currentUserAtom } from "@/store/userAtoms";
import { isSearchPanelOpenAtom } from "@customer/Search/store/searchAtoms";
import { DrawerPanel } from "./DrawerPanel";
import { isMobileDrawerOpenAtom } from "./store/drawerAtoms";

type SubPanel = null | "products" | "account";

const MARKETING_LINKS = [
    { label: "About", to: ROUTES.ABOUT },
    { label: "Support", to: ROUTES.SUPPORT },
    { label: "Terms", to: ROUTES.TERMS },
    { label: "Privacy", to: ROUTES.PRIVACY },
] as const;

export const MobileDrawer = () => {
    const [isOpen, setIsOpen] = useAtom(isMobileDrawerOpenAtom);
    const [subPanel, setSubPanel] = useState<SubPanel>(null);
    const drawerRef = useRef<HTMLElement>(null);
    const { isAuthenticated, instance } = useAuth();
    const appUser = useAtomValue(currentUserAtom);
    const openSearchPanel = useSetAtom(isSearchPanelOpenAtom);
    const signOut = useSignOut();

    // Not-yet-approved users are walled at /c/pending; hide the shopping entry
    // points in the drawer but keep Account (and sign-out) reachable.
    const canShop = useCanShop();

    // Reset drill-down state on every close path so the drawer re-opens to main.
    const handleClose = useCallback(() => {
        setSubPanel(null);
        setIsOpen(false);
    }, [setIsOpen]);

    const handleAccountClick = () => {
        setSubPanel("account");
    };
    const handleSignInClick = () => {
        signInRedirect(instance);
        handleClose();
    };
    const handleSearchClick = () => {
        handleClose();
        openSearchPanel(true);
    };
    // The drill-down buttons (Products, Account, Back) swap their own list
    // out from under focus. The backdrop below is a sibling of the panel, so
    // a drag out of the panel never clicks it: its plain onClick stays.
    const { panelProps } = useDialogFocusTrap({
        panelRef: drawerRef,
        isOpen,
        isProcessing: false,
        onRequestClose: handleClose,
    });

    if (!isOpen) return null;

    return (
        <>
            <div
                onClick={handleClose}
                className="fixed inset-0 bg-black/30 z-40 md:hidden"
            />
            <aside
                ref={drawerRef}
                data-testid="mobile-drawer"
                role="dialog"
                aria-modal="true"
                aria-label="Menu"
                {...panelProps}
                className="fixed inset-y-0 right-0 z-50 w-80 max-w-[85vw] bg-base-100 text-neutral shadow-xl md:hidden flex flex-col focus:outline-none"
            >
                <div className="flex justify-end p-3">
                    <button
                        type="button"
                        aria-label="Close menu"
                        onClick={handleClose}
                        className="btn btn-ghost btn-sm btn-square"
                    >
                        <Icon name="close" className="text-2xl" />
                    </button>
                </div>
                <nav className="flex-1 overflow-y-auto">
                    {subPanel === null && !isAuthenticated && (
                        <>
                            <div className="flex items-center gap-3 px-4 pb-3">
                                {/* TODO: /apply arrives in E5-S4 (#191) */}
                                <Button
                                    to={ROUTES.APPLY}
                                    onClick={handleClose}
                                    color={ComponentColor.Warning}
                                    className="flex-1 text-neutral font-bold normal-case"
                                >
                                    Apply Now
                                </Button>
                                <button
                                    type="button"
                                    onClick={handleSignInClick}
                                    className="flex items-center gap-2 font-bold hover:underline"
                                >
                                    <Icon name="login" className="text-2xl" />
                                    <span>Sign In</span>
                                </button>
                            </div>
                            <ul className="divide-y divide-base-300 border-t border-base-300">
                                {MARKETING_LINKS.map(({ label, to }) => (
                                    <li key={to}>
                                        <Link
                                            to={to}
                                            onClick={handleClose}
                                            className="block px-4 py-3 font-bold hover:bg-base-200"
                                        >
                                            {label}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                    {subPanel === null && isAuthenticated && (
                        <ul className="divide-y divide-base-300">
                            {canShop && (
                                <>
                                    <li>
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setSubPanel("products")
                                            }
                                            className="w-full flex items-center justify-between px-4 py-3 font-bold hover:bg-base-200"
                                        >
                                            <span>Products</span>
                                            <Icon
                                                name="chevron_right"
                                                className="text-xl"
                                            />
                                        </button>
                                    </li>
                                    <li>
                                        <button
                                            type="button"
                                            onClick={handleSearchClick}
                                            className="w-full flex items-center gap-3 px-4 py-3 font-bold hover:bg-base-200"
                                        >
                                            <Icon
                                                name="search"
                                                className="text-2xl"
                                            />
                                            <span>Search</span>
                                        </button>
                                    </li>
                                    <li>
                                        <Link
                                            to={ROUTES.CART}
                                            onClick={handleClose}
                                            className="w-full flex items-center gap-3 px-4 py-3 font-bold hover:bg-base-200"
                                        >
                                            <Icon
                                                name="shopping_cart"
                                                className="text-2xl"
                                            />
                                            <span>Cart</span>
                                        </Link>
                                    </li>
                                </>
                            )}
                            <li>
                                <button
                                    type="button"
                                    onClick={handleAccountClick}
                                    className="w-full flex items-center justify-between px-4 py-3 font-bold hover:bg-base-200"
                                >
                                    <span className="flex items-center gap-3">
                                        <Icon
                                            name="account_circle"
                                            filled
                                            className="text-2xl"
                                        />
                                        Account
                                    </span>
                                    <Icon
                                        name="chevron_right"
                                        className="text-xl"
                                    />
                                </button>
                            </li>
                        </ul>
                    )}
                    {subPanel === "products" && (
                        <DrawerPanel
                            testId="drawer-subpanel-products"
                            onBack={() => setSubPanel(null)}
                        >
                            <div className="px-4 py-4 opacity-60">
                                Coming soon
                            </div>
                        </DrawerPanel>
                    )}
                    {subPanel === "account" && (
                        <DrawerPanel
                            testId="drawer-subpanel-account"
                            onBack={() => setSubPanel(null)}
                        >
                            {appUser && (
                                <div className="px-4 py-3 font-bold opacity-70">
                                    {appUser.firstName} {appUser.lastName}
                                </div>
                            )}
                            <Link
                                to={ROUTES.ACCOUNT}
                                onClick={handleClose}
                                className="w-full flex items-center gap-3 px-4 py-3 font-bold hover:bg-base-200"
                            >
                                <Icon
                                    name="person"
                                    className="text-2xl"
                                />
                                <span>My Account</span>
                            </Link>
                            <button
                                type="button"
                                onClick={signOut}
                                className="w-full flex items-center gap-3 px-4 py-3 font-bold hover:bg-base-200"
                            >
                                <Icon
                                    name="logout"
                                    className="text-2xl"
                                />
                                <span>Logout</span>
                            </button>
                        </DrawerPanel>
                    )}
                </nav>
            </aside>
        </>
    );
};
